import type { User } from '@repo-level/core-front';
import type {
  ICloverGoldLvlProgress,
  IPromoDate,
} from 'api/cms/cloverGoldPromo';
import { groupMemberCommonVIP, USER_GROUP } from 'helpers/user';
import moment from 'moment';

export const PROGRESS_BREAKPOINTS_CLOVER_GOLD = [0, 25, 50, 75, 100].map(
  (percentage) => ({ percentage, title: `${percentage}` })
);

export const CLOVER_GOLD_RESTRICTED_GROUPS = [
  USER_GROUP.BA,
  USER_GROUP.BR,
  USER_GROUP.LOW_BET_LIMIT,
];

export enum ECloverGoldCasualBonusGroupKey {
  L0 = 'clover_0%', // promo start
  L1 = 'clover_25%',
  L2 = 'clover_50%',
  L3 = 'clover_75%',
  L4 = 'clover_100%', // bonus
  L0_R = 'clover_125%', // second round
  L1_R = 'clover_150%',
  L2_R = 'clover_175%',
  L3_R = 'clover_200%', // bonus
}

export enum ECloverGoldVIPBonusGroupKey {
  L0 = 'vip_clover_0%', // promo start
  L1 = 'vip_clover_25%',
  L2 = 'vip_clover_50%',
  L3 = 'vip_clover_75%',
  L4 = 'vip_clover_100%', // bonus
  L0_R = 'vip_clover_125%', // second round
  L1_R = 'vip_clover_150%',
  L2_R = 'vip_clover_175%',
  L3_R = 'vip_clover_200%', // bonus
}

export const CLOVER_VIP_LOOTBOX_GROUP_KEY = 'patrick_lootbox_vip';
export const CLOVER_CASUAL_LOOTBOX_GROUP_KEY = 'patrick_lootbox_casual';
export const CLOVER_VIP_WHEEL_GROUP_KEY = 'vip_respawn_lootbox_manual1';
export const CLOVER_CASUAL_WHEEL_GROUP_KEY = 'new_groups_updated_lootbox';

const cloverGoldCasualGroupKeys = Object.values(
  ECloverGoldCasualBonusGroupKey
) as string[];

const cloverGoldVIPGroupKeys = Object.values(
  ECloverGoldVIPBonusGroupKey
) as string[];

export const getCloverGoldGroupKey = (
  user?: User
): ECloverGoldCasualBonusGroupKey | ECloverGoldVIPBonusGroupKey => {
  const isVIP = groupMemberCommonVIP(user);
  const groupKeys = isVIP ? cloverGoldVIPGroupKeys : cloverGoldCasualGroupKeys;

  const defaultKey = isVIP
    ? ECloverGoldVIPBonusGroupKey.L0
    : ECloverGoldCasualBonusGroupKey.L0;

  const matches = user?.statuses?.filter(({ id }) => groupKeys.includes(id));
  const lastMatch = [...groupKeys]
    .reverse()
    .find((key) => matches?.some(({ id }) => id === key));

  return (
    (lastMatch as
      | ECloverGoldCasualBonusGroupKey
      | ECloverGoldVIPBonusGroupKey) ?? defaultKey
  );
};

export const getCloverGoldAssets = (
  user?: User,
  lvlProgress?: ICloverGoldLvlProgress[]
): ICloverGoldLvlProgress | undefined => {
  if (!lvlProgress?.length) return;

  const key = getCloverGoldGroupKey(user);
  const percentage = Number.parseInt(
    key.replace(/^(vip_clover_|clover_)/, ''),
    10
  );

  const cmsLvl = `L_${percentage}`;

  return lvlProgress.find((item) => item.lvl === cmsLvl) ?? lvlProgress[0];
};

export const isCloverGoldLvl3 = (user?: User): boolean => {
  const key = getCloverGoldGroupKey(user);
  return (
    key === ECloverGoldCasualBonusGroupKey.L3 ||
    key === ECloverGoldVIPBonusGroupKey.L3
  );
};

export const isCloverGoldLvl4 = (user?: User): boolean => {
  const key = getCloverGoldGroupKey(user);

  return [
    ECloverGoldCasualBonusGroupKey.L4,
    ECloverGoldCasualBonusGroupKey.L3_R,
    ECloverGoldVIPBonusGroupKey.L4,
    ECloverGoldVIPBonusGroupKey.L3_R,
  ].includes(key);
};

export const isCloverGoldFirstRoundBonus = (user?: User): boolean => {
  const key = getCloverGoldGroupKey(user);
  return (
    key === ECloverGoldCasualBonusGroupKey.L4 ||
    key === ECloverGoldVIPBonusGroupKey.L4
  );
};

export const isPromoLastLvl = (user?: User): boolean => {
  const key = getCloverGoldGroupKey(user);

  return [
    ECloverGoldCasualBonusGroupKey.L3_R,
    ECloverGoldVIPBonusGroupKey.L3_R,
  ].includes(key);
};

export const getCloverGoldProgress = (user?: User): number => {
  const key = getCloverGoldGroupKey(user);

  const total = Number.parseInt(key.replace(/^(vip_clover_|clover_)/, ''), 10);
  return total > 100 ? total - 100 : total;
};

export const isCloverGoldSecondRound = (promoDate?: IPromoDate): boolean => {
  if (!promoDate) return false;
  return moment.utc().isAfter(moment.utc(promoDate.endDate));
};
